var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as THREE from 'three';
import Stats from 'three/examples/jsm/libs/stats.module';
import '../services/creation/worldFiles';
import SceneManager from '../core/SceneManager';
import * as AbilityHUD from '../services/hud/AbilityHUD';
import * as DragHandler from '../services/interaction/DragHander';
import * as AttractionSystem from '../services/physics/AttractionSystem';
import { OrbConnectionManager } from '../services/connections/OrbConnectionManager';
import { summonedOrbitController } from '../services/abilities/SummonedOrbitController';
import { ringOrbitAnimationRegistry } from '../utils/RingLayout';
import { global } from '../core/GlobalState';
import { registerActionHandlers } from '../services/actions/OrbActionHandlers';
import { registerWebGLContextLifecycle } from '../services/systemState/WebGL-Lifecycle';
import { applyCrashLoopRecovery, applyPendingRecovery, getSafeModeReason, isSafeModeEnabled, } from '../services/systemState/CrashRecovery';
import * as SceneSwitching from '../services/portals/SceneSwitching';
import * as Registries from '../orbs/RegisterOrbs';
import * as AppStateManager from '../core/AppStateManager';
import { PlayerSystem } from '../core/player/PlayerSystem';
import { HighPerformanceRuntime, probeRenderWorkerSupport, startRenderWorkerSession, } from '../core/highperformance';
//import '../services/creation/worldFiles'
import { SystemWorld } from '../services/creation/worldFiles/SystemWorld';
import { playerManager } from '../core/player/PlayerManager';
import { bootstrapInitialOrbsFromSsi } from '../services/bootstrap/InitialOrbBootstrap';
// Initialize SceneManager
const sceneManager = SceneManager.getInstance();
const highPerformanceRuntime = HighPerformanceRuntime.create({ enableInstrumentation: true });
global.highPerformanceRuntime = highPerformanceRuntime;
//ThreeJS objects
let scene;
let renderer;
let composer;
let renderPass;
let localPlayerRuntime;
const playerSystem = new PlayerSystem({
    domParent: document.body,
    isMobile: AppStateManager.isMobile(),
});
sceneManager.on('currentChanged', ({ sceneName }) => {
    const nextScene = sceneManager.getScene(sceneName);
    scene = nextScene;
    global.scene = nextScene;
    playerSystem.ensureLocalClientOrb(sceneName);
});
const applySafeModeSettings = () => {
    var _a;
    global.safeModeEnabled = true;
    global.safeModeReason = getSafeModeReason();
    global.rendererPixelMode = 'performance';
    global.maxRendererPixelRatio = global.reducedMaxRendererPixelRatio;
    console.warn('[Recovery] Safe mode enabled', {
        reason: (_a = global.safeModeReason) !== null && _a !== void 0 ? _a : 'unknown',
    });
};
if (isSafeModeEnabled()) {
    applySafeModeSettings();
}
const resolveDataWorkerFlag = () => {
    var _a;
    if (typeof window === 'undefined')
        return false;
    try {
        const params = new URLSearchParams((_a = window.location.search) !== null && _a !== void 0 ? _a : '');
        const queryFlag = params.get('dataWorker');
        if (queryFlag === '1' || queryFlag === 'true')
            return true;
        if (queryFlag === '0' || queryFlag === 'false')
            return false;
        return window.localStorage.getItem('orbscape:enableDataWorker') === 'true';
    }
    catch (error) {
        console.warn('[SystemAPI] Failed to resolve data worker flag', error);
        return false;
    }
};
global.dataWorkerEnabled = resolveDataWorkerFlag() && !global.safeModeEnabled;
const resolveRenderWorkerFlag = () => {
    var _a;
    if (typeof window === 'undefined')
        return false;
    try {
        const params = new URLSearchParams((_a = window.location.search) !== null && _a !== void 0 ? _a : '');
        const queryFlag = params.get('renderWorker');
        if (queryFlag === '1' || queryFlag === 'true')
            return true;
        if (queryFlag === '0' || queryFlag === 'false')
            return false;
        return window.localStorage.getItem('orbscape:enableRenderWorker') === 'true';
    }
    catch (error) {
        console.warn('[RenderWorker] Failed to resolve render worker flag', error);
        return false;
    }
};
global.renderWorkerEnabled = resolveRenderWorkerFlag() && !global.safeModeEnabled;
const runRenderWorkerProbe = () => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c;
    if (!global.renderWorkerEnabled)
        return;
    try {
        const canvas = (_a = renderer === null || renderer === void 0 ? void 0 : renderer.domElement) !== null && _a !== void 0 ? _a : null;
        const result = yield probeRenderWorkerSupport(canvas);
        global.renderWorkerSupported = result.supported;
        global.renderWorkerSupportReason = (_b = result.reason) !== null && _b !== void 0 ? _b : null;
        global.renderWorkerWebglSupported = result.webglSupported;
        if (!result.supported) {
            console.warn('[RenderWorker] Render worker unavailable; continuing on main thread.', {
                reason: (_c = result.reason) !== null && _c !== void 0 ? _c : 'unknown',
                offscreenCanvasSupported: result.offscreenCanvasSupported,
                canvasTransferSupported: result.canvasTransferSupported,
                webglSupported: result.webglSupported,
                workerCanvasTransferSupported: result.workerCanvasTransferSupported,
            });
            return;
        }
        if (global.renderWorkerSession) {
            global.renderWorkerSession.shutdown();
            global.renderWorkerSession = null;
        }
        try {
            global.renderWorkerSession = yield startRenderWorkerSession({
                devicePixelRatio: window.devicePixelRatio || 1,
                width: window.innerWidth,
                height: window.innerHeight,
            }, undefined, {
                onContextLost: () => {
                    global.renderWorkerContextLost = true;
                    console.warn('[RenderWorker] Worker context lost; session suspended.');
                },
                onContextRestored: () => {
                    global.renderWorkerContextLost = false;
                    console.warn('[RenderWorker] Worker context restored; session resumed.');
                },
            });
            global.renderWorkerSession.setSuspended(document.hidden);
            console.warn('[RenderWorker] Render worker session started (scaffold only); continuing on main thread.');
        }
        catch (initError) {
            global.renderWorkerSupported = false;
            global.renderWorkerSupportReason = 'render worker init failed';
            console.warn('[RenderWorker] Failed to initialize render worker session', initError);
        }
    }
    catch (error) {
        global.renderWorkerSupported = false;
        global.renderWorkerSupportReason = 'probe error';
        console.warn('[RenderWorker] Failed to probe render worker support', error);
    }
});
const registerRenderWorkerShutdown = () => {
    if (typeof window === 'undefined')
        return;
    const shutdown = () => {
        if (!global.renderWorkerSession)
            return;
        global.renderWorkerSession.shutdown();
        global.renderWorkerSession = null;
    };
    window.addEventListener('pagehide', shutdown);
    window.addEventListener('beforeunload', shutdown);
};
const registerRenderWorkerVisibility = () => {
    if (typeof document === 'undefined')
        return;
    const updateSuspended = () => {
        var _a;
        (_a = global.renderWorkerSession) === null || _a === void 0 ? void 0 : _a.setSuspended(document.hidden);
    };
    document.addEventListener('visibilitychange', updateSuspended);
    updateSuspended();
};
// Ensure any previous crash-triggered cleanup runs before heavy work
const pendingRecovery = (() => __awaiter(void 0, void 0, void 0, function* () {
    yield applyPendingRecovery();
    const state = yield applyCrashLoopRecovery();
    if (state.crashLoopDetected || isSafeModeEnabled()) {
        applySafeModeSettings();
    }
}))().catch(err => {
    console.warn('[Recovery] pending cleanup failed', err);
});
function setActiveScene(sceneName) {
    const resolvedName = sceneName || sceneManager.getCurrentSceneName();
    sceneManager.setCurrentScene(resolvedName);
    const nextScene = sceneManager.getScene(resolvedName);
    scene = nextScene;
    global.scene = nextScene;
}
function getInitialWorldSlug() {
    var _a, _b;
    if (typeof window === 'undefined')
        return null;
    const rawPath = (_b = (_a = window.location) === null || _a === void 0 ? void 0 : _a.pathname) !== null && _b !== void 0 ? _b : '';
    if (!rawPath || rawPath === '/')
        return null;
    if (rawPath.endsWith('/'))
        return null;
    const segments = rawPath.split('/').filter(Boolean);
    if (segments.length !== 1)
        return null;
    return decodeURIComponent(segments[0]).toLowerCase();
}
function switchToWorldSlug(slug, options = {}) {
    var _a, _b, _c, _d;
    if (!slug)
        return false;
    let world = global.worldRegistry.getWorld(slug);
    if (!world) {
        world = (_a = global.worldRegistry.ensureDynamicWorld(slug)) !== null && _a !== void 0 ? _a : undefined;
    }
    if (!world)
        return false;
    global.worldRegistry.ensureScene(world.slug);
    const targetScene = world.sceneName;
    const currentScene = sceneManager.getCurrentSceneName();
    const alreadyActive = currentScene === targetScene;
    const suppressHistory = (_b = options.suppressHistoryUpdate) !== null && _b !== void 0 ? _b : false;
    if (suppressHistory)
        global.routing.suppressHistoryUpdate = true;
    try {
        if (!alreadyActive) {
            SceneSwitching.switchScene(targetScene, { unloadTwoHops: (_c = options.unloadTwoHops) !== null && _c !== void 0 ? _c : true });
        }
        else {
            setActiveScene(targetScene);
        }
    }
    finally {
        if (suppressHistory)
            global.routing.suppressHistoryUpdate = false;
    }
    if (options.replaceHistory && typeof window !== 'undefined') {
        const desiredPath = `/${world.slug}`;
        if (((_d = window.history) === null || _d === void 0 ? void 0 : _d.replaceState) && window.location.pathname !== desiredPath) {
            try {
                window.history.replaceState({ world: world.slug }, '', desiredPath);
            }
            catch (error) {
                console.warn('[Routing] Failed to replace history state', error);
            }
        }
    }
    return true;
}
function ensureInitialWorldFromPath() {
    const slug = getInitialWorldSlug();
    if (!slug) {
        global.worldRegistry.ensureScene('system');
        switchToWorldSlug('system', { suppressHistoryUpdate: true, replaceHistory: true, unloadTwoHops: false });
        return;
    }
    if (!global.worldRegistry.hasWorld(slug)) {
        global.worldRegistry.ensureDynamicWorld(slug);
    }
    switchToWorldSlug(slug, { suppressHistoryUpdate: true, replaceHistory: true, unloadTwoHops: false });
}
function navigateToPathSlug() {
    var _a;
    if (typeof window === 'undefined')
        return;
    const slug = getInitialWorldSlug();
    if (slug) {
        if (!global.worldRegistry.hasWorld(slug)) {
            global.worldRegistry.ensureDynamicWorld(slug);
        }
        if (switchToWorldSlug(slug, { suppressHistoryUpdate: true })) {
            return;
        }
    }
    const fallbackSlug = 'system';
    switchToWorldSlug(fallbackSlug, { suppressHistoryUpdate: true, unloadTwoHops: false });
    if (((_a = window.history) === null || _a === void 0 ? void 0 : _a.replaceState) && window.location.pathname !== '/system') {
        try {
            window.history.replaceState({}, '', '/system');
        }
        catch (error) {
            console.warn('[Routing] Failed to restore root path', error);
        }
    }
}
function syncPostProcessingHandles() {
    const nextComposer = global.composer;
    if (nextComposer && nextComposer !== composer) {
        composer = nextComposer;
    }
    const nextRenderPass = global.renderPass;
    if (nextRenderPass && nextRenderPass !== renderPass) {
        renderPass = nextRenderPass;
    }
}
//Time delta
let prevTime = performance.now();
//Stats
const stats = new Stats();
//TODO MOVE TO GLOBAL CONFIG
const arenaSize = 700;
//TODO MOVE TO GLOBAL CONFIG
const startSprayPosition = new THREE.Vector3(2000, 5000, -2000);
// Global variables
//let orbManager: OrbManager = OrbManager.getInstance();
//let clickMode: AbilityMode = AbilityMode.InspectObject;
//let currentObject: THREE.Object3D;
//let apiButtonOrbId: string = "";
// I want to have some kind of fzf like cli experience with this
//const chat = new ChatWindow();
//move this, need a global reflective orbs somewhere
// store reflective orbs so we can update them each frame
//const reflectiveOrbs: { mesh: THREE.Mesh; cubeCamera: THREE.CubeCamera }[] = [];
function init() {
    return __awaiter(this, void 0, void 0, function* () {
        console.log('[Init] Starting application bootstrap');
        // 1. Scene
        scene = sceneManager.getScene();
        scene.background = new THREE.Color(0xefedfa); //0x7F87F8
        scene.fog = new THREE.Fog(0xffffff, 0, arenaSize * 30);
        console.log('[Init] Scene primed with base fog/background');
        // 2. Player + renderer + controls
        const performanceMaxDpr = 1.5;
        const crispMaxDpr = Math.max(window.devicePixelRatio || 1, 2.5);
        localPlayerRuntime = playerSystem.createLocalPlayer({
            id: 'local',
            startScene: sceneManager.getCurrentSceneName(),
            renderer: {
                antialias: true,
                powerPreference: 'high-performance',
                logarithmicDepthBuffer: true,
            },
        });
        renderer = localPlayerRuntime.view.renderer;
        composer = localPlayerRuntime.view.composer;
        renderPass = localPlayerRuntime.view.renderPass;
        global.reducedMaxRendererPixelRatio = performanceMaxDpr;
        global.legacyMaxRendererPixelRatio = crispMaxDpr;
        global.maxRendererPixelRatio = crispMaxDpr;
        global.rendererPixelMode = 'crisp';
        registerWebGLContextLifecycle(renderer, {
            onContextLost: () => {
                var _a;
                prevTime = performance.now();
                (_a = global.renderWorkerSession) === null || _a === void 0 ? void 0 : _a.setSuspended(true);
            },
            onContextRestored: () => {
                var _a;
                // The lifecycle util recreates global references – keep locals aligned
                syncPostProcessingHandles();
                prevTime = performance.now();
                (_a = global.renderWorkerSession) === null || _a === void 0 ? void 0 : _a.setSuspended(document.hidden);
            },
        });
        console.log('[Init] Renderer attached', {
            canvasSize: { width: window.innerWidth, height: window.innerHeight },
        });
        runRenderWorkerProbe().catch((error) => {
            console.warn('[RenderWorker] Failed to run render worker probe', error);
        });
        registerRenderWorkerShutdown();
        registerRenderWorkerVisibility();
        console.log('[Init] Controls configured', { mode: AppStateManager.isMobile() ? 'touch' : 'pointer-lock' });
        // 3. Postprocessing passes (composer, renderPass, outlinePass)
        AppStateManager.applyRendererPixelRatio();
        renderer.setSize(window.innerWidth, window.innerHeight);
        if (typeof composer.setPixelRatio === 'function') {
            composer.setPixelRatio(renderer.getPixelRatio());
        }
        console.log('[Init] Post-processing pipeline ready');
        // assign everything into global after fully initialized
        global.scene = scene;
        global.composer = composer;
        global.renderPass = renderPass;
        syncPostProcessingHandles();
        console.log('[Init] Global references synced');
        console.log('[Init] Input handlers wired');
        //LIGHTING
        const skyColor = 0x9da4f5;
        const groundColor = 0x7F87F8;
        const intensity = 1.00;
        const light = new THREE.HemisphereLight(skyColor, 0x9da4f5, intensity);
        //var light2 = new THREE.PointLight(0xffffff);
        //light2.position.set(0,250,0);     
        scene.add(light);
        console.log('[Init] Lighting setup complete');
        //EVENTS
        AppStateManager.blocker();
        AppStateManager.onWindowResize();
        console.log('[Init] UI overlays and events registered');
        //ENVIRONMENT
        //WorldFactory.buildDefaultWorld(scene);
        SystemWorld.build(scene);
        console.log('[Init] Default world populated');
        ensureInitialWorldFromPath();
        playerSystem.ensureLocalClientOrb(sceneManager.getCurrentSceneName());
        if (typeof window !== 'undefined') {
            window.addEventListener('popstate', navigateToPathSlug);
        }
        global.chat.addMessage("[Object Explorer Command Line]");
        console.log('[Init] Chat primed with welcome message');
        //await OrbFactory.initializeOrbs();
        AbilityHUD.createAbilityBar();
        console.log('[Init] Ability HUD initialized');
        console.log('[Init] Bootstrap complete');
        //console.log("Hello " + OrbTypes.API_ORB);
        //generateSystemOrbsTest();
        //await generateCodeOrbs();
        //TODO move to another file
        //this lives on global
        Registries.registerOrbs();
        registerActionHandlers();
        bootstrapInitialOrbsFromSsi().catch((error) => {
            console.warn('[Init] Failed to bootstrap SSI orbs', error);
        });
        //TODO MOVE!!!
        // make it much larger
        /*
        const radius = 600;     // was 200 before
      
      
        const { mesh: mirrorSphere, cubeCamera } = createReflectiveSphere(radius, nearPlane, farPlane, 1024);
      
        // position it higher in Y
        mirrorSphere.position.set(7000, 5000, 0);
      
        scene.add(mirrorSphere);
        reflectiveOrbs.push({ mesh: mirrorSphere, cubeCamera });
        */
    });
}
function animate() {
    var _a;
    requestAnimationFrame(animate);
    const now = performance.now();
    highPerformanceRuntime.frame(now);
    (_a = global.renderWorkerSession) === null || _a === void 0 ? void 0 : _a.frame(now);
    if (global.__webglLost)
        return; // <- do nothing while lost
    render();
    update();
}
function render() {
    var _a, _b;
    syncPostProcessingHandles();
    const activeComposer = (_a = global.composer) !== null && _a !== void 0 ? _a : composer;
    if (activeComposer) {
        activeComposer.render();
        return;
    }
    const camera = (_b = localPlayerRuntime === null || localPlayerRuntime === void 0 ? void 0 : localPlayerRuntime.player) === null || _b === void 0 ? void 0 : _b.camera;
    if (renderer && scene && camera) {
        renderer.render(scene, camera);
    }
}
function update() {
    const time = performance.now();
    const rawDeltaSeconds = Math.max(0, (time - prevTime) / 1000);
    const clampedSeconds = Math.min(rawDeltaSeconds, 0.05);
    const localPlayer = playerManager.localPlayer;
    if (localPlayer) {
        localPlayer.update(clampedSeconds);
    }
    ringOrbitAnimationRegistry.update(clampedSeconds, scene);
    if (localPlayer === null || localPlayer === void 0 ? void 0 : localPlayer.input.isLocked) {
        global.orbManager.updateOrbMotion();
        // keep lines glued to orb surfaces:
        OrbConnectionManager.getInstance().update(clampedSeconds); // use seconds-based timestep
        //THese both early return internally
        DragHandler.updateDraggedOrb();
        AttractionSystem.attractAllOrbs();
        summonedOrbitController.update(clampedSeconds, (orbId) => {
            const orb = global.orbManager.getOrb(orbId);
            return (orb === null || orb === void 0 ? void 0 : orb.group) ? orb.group.position.clone() : null;
        }, (orbId, position) => {
            const orb = global.orbManager.getOrb(orbId);
            if (orb) {
                orb.setPosition(position.clone());
            }
        });
    }
    prevTime = time;
}
init()
    .then(() => {
    highPerformanceRuntime.start();
    animate();
})
    .catch((error) => {
    console.error('Failed to initialize application', error);
});
// Register Service Worker for PWA offline support on mobile devices only
(() => {
    // hard guard for SSR / non-browser builds
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return;
    }
    // navigator in this file is sometimes typed as WorkerNavigator,
    // so we widen it to include serviceWorker safely.
    const navWithSW = navigator;
    const isMobileClient = () => {
        // basic UA check with optional UA hints
        if (navWithSW.userAgentData && typeof navWithSW.userAgentData.mobile === 'boolean') {
            return !!navWithSW.userAgentData.mobile;
        }
        return /android|iphone|ipad|ipod|iemobile|windows phone|mobile/i.test(navWithSW.userAgent || '');
    };
    // if this browser doesn't even expose serviceWorker, bail
    if (!('serviceWorker' in navWithSW) || !navWithSW.serviceWorker) {
        return;
    }
    window.addEventListener('load', () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        yield pendingRecovery;
        if (global.safeModeEnabled) {
            console.warn('[Recovery] Skipping service worker registration in safe mode.');
            return;
        }
        // DESKTOP PATH: actively unregister and clear caches
        if (!isMobileClient()) {
            try {
                // explicitly typed so TS doesn't complain about implicit any
                const registrations = yield navWithSW.serviceWorker.getRegistrations();
                const hadRegistrations = registrations.length > 0;
                yield Promise.all(registrations.map((registration) => registration.unregister()));
                if ('caches' in window) {
                    const cacheKeys = yield caches.keys();
                    yield Promise.all(cacheKeys
                        .filter((key) => key.startsWith('orbscape-cache'))
                        .map((key) => caches.delete(key)));
                }
                if (hadRegistrations && ((_a = navWithSW.serviceWorker) === null || _a === void 0 ? void 0 : _a.controller)) {
                    console.log('Service worker disabled for desktop client; reloading without cache control.');
                    window.location.reload();
                }
                else {
                    console.log('Skipping service worker registration for desktop client.');
                }
            }
            catch (err) {
                console.warn('Unable to disable service worker on desktop client:', err);
            }
            return;
        }
        // MOBILE PATH: register PWA service worker
        navWithSW.serviceWorker
            .register('/serviceWorker.js')
            .then((registration) => {
            console.log('Service Worker registered with scope:', registration.scope);
            // Auto-update immediately if new service worker is available
            registration.onupdatefound = () => {
                const newWorker = registration.installing;
                newWorker === null || newWorker === void 0 ? void 0 : newWorker.addEventListener('statechange', () => {
                    var _a;
                    if (newWorker.state === 'installed' &&
                        ((_a = navWithSW.serviceWorker) === null || _a === void 0 ? void 0 : _a.controller)) {
                        console.log('New content available, updating...');
                        window.location.reload();
                    }
                });
            };
        })
            .catch((err) => {
            console.error('Service Worker registration failed:', err);
        });
    }));
})();
